import { toUnixEpochTime, normalizeDateFormat } from "utils/date-format";
import type { RegisterDocumentRequest } from "components/create-audit-form/definitions";

/**
 * Register a scanned document with the system
 */
export const registerScanDocument = async (
  shortId: string | undefined,
  scanJobData: any,
  noticeId: string,
  teamName: string,
  registerDocumentCall: any
): Promise<string | null> => {
  if (!shortId || !scanJobData?.scanJob?.inputS3Key) {
    return null;
  }

  try {
    const registerDocumentRequest: RegisterDocumentRequest = {
      parentId: noticeId,
      gaiaObjectId: noticeId,
      teamName: teamName,
      fileName: scanJobData.scanJob.fileName,
      documentType: "Notice letters",
      securityLevel: "Normal",
      extentionType: scanJobData.scanJob.extensionType,
      s3Prefix: scanJobData.scanJob.inputS3Key,
      size: scanJobData.scanJob.size,
    };

    await registerDocumentCall(registerDocumentRequest).unwrap();
    return null;
  } catch (error: any) {
    console.error("Failed to register scan job document:", error);
    return "Failed to attach scanned file to the notice";
  }
};

/**
 * Update scan job status to "Review complete"
 */
export const updateScanStatus = async (
  shortId: string | undefined,
  noticeId: string,
  updateScanJobCall: any
): Promise<string | null> => {
  if (!shortId) {
    return null;
  }

  try {
    await updateScanJobCall({
      shortId: shortId,
      gaiaObjectId: noticeId,
      status: "Review complete",
    }).unwrap();
    return null;
  } catch (error: any) {
    console.error("Failed to update scan job:", error);
    return "Failed to mark scan job as complete";
  }
};

/**
 * Maps raw tax year breakdown items to the API request shape.
 */
function mapTaxYearBreakdowns(parsedTaxYearBreakdowns: any[]): any[] {
  return (parsedTaxYearBreakdowns || []).map((b: any) => ({
    totalTaxAmount: b.totalTaxAmount || "0",
    totalPenaltyAmount: b.totalPenaltyAmount || "0",
    totalInterestAmount: b.totalInterestAmount || "0",
    totalOtherAmount: b.totalOtherAmount || "0",
    type: b.type,
    category: b.category,
  }));
}

/**
 * Maps raw tax year & area breakdown items to the API request shape.
 * Optionally converts dueDate strings to unix epoch.
 */
function mapTaxYearAreaBreakdowns(
  parsedTaxYearAreaBreakdowns: any[],
  convertDueDate = false
): any[] {
  return (parsedTaxYearAreaBreakdowns || []).map((b: any) => ({
    totalTaxAmount: b.totalTaxAmount || "0",
    totalPenaltyAmount: b.totalPenaltyAmount || "0",
    totalInterestAmount: b.totalInterestAmount || "0",
    totalOtherAmount: b.totalOtherAmount || "0",
    additionalInformation: b.additionalInformation || "",
    dueDate: convertDueDate
      ? b.dueDate
        ? toUnixEpochTime(normalizeDateFormat(b.dueDate))
        : undefined
      : b.dueDate || "",
    type: b.type,
    category: `${b.taxYear || ""}${b.taxAreaId || ""}`,
  }));
}

/**
 * Create an assessment from localStorage data
 */
export const createAssessmentFromData = async (
  noticeId: string,
  assessmentStorageKey: string,
  shortId: string | undefined,
  isAssessmentSimplified: boolean,
  createAssessment: any
): Promise<{ success: boolean; error: string | null }> => {
  const assessmentDataStr = localStorage.getItem(assessmentStorageKey);
  if (!assessmentDataStr || !shortId || isAssessmentSimplified) {
    return { success: false, error: null };
  }

  try {
    const {
      type,
      dateReceived,
      notes,
      amounts,
      parsedTaxYearBreakdowns,
      parsedTaxYearAreaBreakdowns,
    } = JSON.parse(assessmentDataStr);

    const assessmentRequest = {
      gaiaObjectId: noticeId,
      type: type || "Preliminary",
      dateReceived: dateReceived
        ? toUnixEpochTime(normalizeDateFormat(dateReceived))
        : Math.floor(Date.now() / 1000),
      totalTaxAmount: amounts?.totalTaxAmount || "0",
      totalPenaltyAmount: amounts?.totalPenaltyAmount || "0",
      totalInterestAmount: amounts?.totalInterestAmount || "0",
      totalOtherAmount: amounts?.totalOtherAmount || "0",
      notes: notes || "",
      taxAreaBreakdown: [],
      taxYearBreakdown: mapTaxYearBreakdowns(parsedTaxYearBreakdowns),
      taxYearAreaBreakdown: mapTaxYearAreaBreakdowns(
        parsedTaxYearAreaBreakdowns,
        true
      ),
    };

    await createAssessment(assessmentRequest).unwrap();
    return { success: true, error: null };
  } catch (error: any) {
    console.error("Error creating assessment:", error);
    return {
      success: false,
      error: error?.message || "Failed to create assessment",
    };
  }
};

/**
 * Create a payment from localStorage data (used in DraftOverview for germany_rc category=assessment)
 */
export const createPaymentFromData = async (
  gaiaObjectId: string,
  paymentStorageKey: string,
  shortId: string | undefined,
  isPaymentSimplified: boolean,
  createPayment: any
): Promise<{ success: boolean; error: string | null }> => {
  const paymentDataStr = localStorage.getItem(paymentStorageKey);
  if (!paymentDataStr || !shortId || isPaymentSimplified) {
    return { success: false, error: null };
  }

  try {
    const {
      type,
      paymentDate,
      notes,
      amounts,
      paymentRefNumber,
      trackingRefNumber,
      paymentAuthorizationLink,
      parsedTaxYearBreakdowns,
      parsedTaxYearAreaBreakdowns,
    } = JSON.parse(paymentDataStr);

    const paymentRequest = {
      gaiaObjectId,
      type: type || "Preliminary",
      paymentDate: paymentDate
        ? toUnixEpochTime(normalizeDateFormat(paymentDate))
        : Math.floor(Date.now() / 1000),
      totalTaxAmount: amounts?.totalTaxAmount || "0",
      totalPenaltyAmount: amounts?.totalPenaltyAmount || "0",
      totalInterestAmount: amounts?.totalInterestAmount || "0",
      totalOtherAmount: amounts?.totalOtherAmount || "0",
      notes: notes || "",
      paymentRefNumber: paymentRefNumber || "",
      trackingRefNumber: trackingRefNumber || "",
      paymentAuthorizationLink: paymentAuthorizationLink || "",
      taxAreaBreakdown: [],
      taxYearBreakdown: mapTaxYearBreakdowns(parsedTaxYearBreakdowns),
      taxYearAreaBreakdown: mapTaxYearAreaBreakdowns(
        parsedTaxYearAreaBreakdowns,
        true
      ),
    };

    await createPayment(paymentRequest).unwrap();
    return { success: true, error: null };
  } catch (error: any) {
    console.error("Error creating payment:", error);
    return {
      success: false,
      error: error?.message || "Failed to create payment",
    };
  }
};

/**
 * Generate notification configuration based on operation results
 */
export const getNotificationConfig = (
  noticeTitle: string,
  assessmentResult: { success: boolean; error: string | null },
  warnings: string[]
) => {
  const hasWarnings = warnings.length > 0;
  const warningsList = warnings.map((w) => `• ${w}`).join("\n");

  // All successful
  if (assessmentResult.success && !hasWarnings) {
    return {
      type: "success" as const,
      header: "Notice and Assessment created successfully",
      content: `Notice "${noticeTitle}" and associated assessment have been created successfully.`,
    };
  }

  // Assessment and notice created, but with warnings
  if (assessmentResult.success && hasWarnings) {
    return {
      type: "warning" as const,
      header: "Notice and Assessment created with warnings",
      content: `Notice "${noticeTitle}" and assessment were created, but some operations failed:\n${warningsList}`,
    };
  }

  // Assessment failed, with or without warnings
  if (assessmentResult.error) {
    const messages = [`• Assessment creation: ${assessmentResult.error}`];
    if (hasWarnings) {
      messages.push(...warnings.map((w) => `• ${w}`));
    }
    return {
      type: "warning" as const,
      header: "Notice created with issues",
      content: `Notice "${noticeTitle}" was created, but encountered the following issues:\n${messages.join(
        "\n"
      )}`,
    };
  }

  // Notice created, possibly with warnings
  if (hasWarnings) {
    return {
      type: "warning" as const,
      header: "Notice created with warnings",
      content: `Notice "${noticeTitle}" was created, but some operations failed:\n${warningsList}`,
    };
  }

  // Only notice created successfully
  return {
    type: "success" as const,
    header: "Notice created successfully",
    content: `Notice "${noticeTitle}" has been created successfully.`,
  };
};
